import { annotationsFetch } from 'packages/lib/api/annotations/rest';

type OverlapScene = {
  log_name: string;
  timestamp: number;
};

type OverlapAPIResponse = {
  logName: string;
  timestamp: number;
  exists: boolean;
}[];

/**
 * Checks a list of scenes for overlap with existing annotation jobs.
 * @param scenes - An array of scene objects to check.
 * @param duration - The duration in seconds for the overlap check.
 * @param hz - The sampling frequency in Hz for the overlap check.
 * @param pipelineToken - The optional pipeline token to filter by.
 * @returns A Map where the key is a scene identifier (log_name-timestamp) and the value is a boolean indicating if it overlaps.
 */
export const checkOverlap = async (
  scenes: OverlapScene[],
  duration: number,
  hz: number,
  pipelineToken: string | null
): Promise<Map<string, boolean>> => {
  if (scenes.length === 0) {
    return new Map();
  }

  const queryParams = new URLSearchParams({
    duration: duration.toString(),
    hz: hz.toString(),
  });

  if (pipelineToken) {
    queryParams.set('pipelineToken', pipelineToken);
  }

  const url = `/status/lidar?${queryParams.toString()}`;

  const body = scenes.map((scene) => ({
    LogName: scene.log_name,
    Timestamp: scene.timestamp,
  }));

  const response = await annotationsFetch(url, 'POST', JSON.stringify(body));

  // The okOrThrow helper in annotationsFetch handles this, but we keep it for type safety
  if (!response.ok) {
    throw new Error(`Overlap check failed with status: ${response.status}`);
  }

  const result = (await response.json()) as OverlapAPIResponse;
  const overlapMap = new Map<string, boolean>();
  for (const item of result) {
    const sceneId = `${item.logName}-${item.timestamp}`;
    overlapMap.set(sceneId, item.exists);
  }
  return overlapMap;
};
